import { useEffect, useMemo, useRef, useState } from 'react';
import { lighten } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { isFinite } from 'lodash';

import { useStatusColors } from '../../../utilities/ColorUtilities';
import { statusDef } from '../../charts/performance-chart/defs';
import { GetTooltip } from '../../charts/tooltip/GetTooltip';
import Tooltip from '../../tooltip/Tooltip';
import Canvas from '../../common/Canvas';

const chartHeight = 14;

const getGranularity = (data) => {
    if (data.length < 2) return 2_000;
    return data[1].date - data[0].date;
};

const statusToTimeslice = (data, point) => {
    const { date } = point;
    const status = point.value?.worst || 'gray';
    const granularity = getGranularity(data);
    const items = [
        {
            def: statusDef.chartTrends[1],
            value: status === 'gray' ? 100 : 0,
        },
        {
            def: statusDef.chartTrends[2],
            value: status === 'green' ? 100 : 0,
        },
        {
            def: statusDef.chartTrends[3],
            value: status === 'yellow' ? 100 : 0,
        },
        {
            def: statusDef.chartTrends[4],
            value: status === 'orange' ? 100 : 0,
        },
        {
            def: statusDef.chartTrends[5],
            value: status === 'red' ? 100 : 0,
        },
    ];

    return {
        items,
        x: date,
        granularity,
    };
};

export function StatusTrend(props) {
    const { data: rawData, width: columnWidth } = props;
    const theme = useTheme();
    const width = columnWidth - theme.spacing(2);
    const statusColors = useStatusColors();
    const data = useMemo(() => (Array.isArray(rawData) ? rawData : []), [rawData]);
    const pointWidth = data && data.length !== 0 ? width / data.length : 0;
    const [, _setSelectedPointIdx] = useState(null);
    const [timeslice, setTimeslice] = useState();

    /** @type {React.MutableRefObject<CanvasRenderingContext2D>} */
    const contextRef = useRef();

    /** @type {React.MutableRefObject<HTMLCanvasElement} */
    const canvasRef = useRef();

    const renderRef = useRef();
    renderRef.current = () => {
        if (!data || !data.length) return;

        const { current: context } = contextRef;

        context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        data.forEach((point, idx) => drawDataPoint(point, idx, context, 'normal', statusColors, pointWidth));
    };

    const setSelectedPointIdx = (pointIdx) => {
        // If there's no point index, clear the time slice (so the tooltip is hidden), and
        // re-draw the entire chart (to get rid of hover antialiasing residue).
        if (pointIdx === null || !isFinite(pointIdx)) {
            setTimeslice(undefined);
            return renderRef.current();
        }

        _setSelectedPointIdx((prevIdx) => {
            // do nothing if we're on the same point
            if (prevIdx !== null && prevIdx === pointIdx) {
                return prevIdx;
            }

            const point = data[pointIdx];
            const prevPoint = data[prevIdx];
            // Unselect the previous point (redraw it)
            if (prevIdx !== null && prevPoint) {
                drawDataPoint(prevPoint, prevIdx, contextRef.current, 'normal', statusColors, pointWidth, true);
            }

            if (point) {
                setTimeslice(statusToTimeslice(data, point));
                drawDataPoint(point, pointIdx, contextRef.current, 'hover', statusColors, pointWidth, true);
            }

            return pointIdx;
        });
    };

    useEffect(() => {
        renderRef.current();
    }, [data, pointWidth, statusColors]);

    const onMouseMove = (/** @type {MouseEvent} */ event) => {
        const { clientX: mouseX } = event;
        const { x: canvasX } = canvasRef.current.getBoundingClientRect();

        const x = mouseX - canvasX;
        const pointIdx = Math.floor(x / pointWidth);

        setSelectedPointIdx(pointIdx);
    };

    return (
        <Tooltip
            open={!!timeslice}
            offset={50}
            title={<GetTooltip chartDef={statusDef} timeslice={timeslice} />}
            placement="left"
        >
            <div>
                <Canvas
                    ref={canvasRef}
                    contextRef={contextRef}
                    height={chartHeight}
                    width={width}
                    onMouseMove={onMouseMove}
                    onMouseOut={() => setSelectedPointIdx(null)}
                />
            </div>
        </Tooltip>
    );
}

/**
 *
 * @param {Object} point
 * @param {number} index
 * @param {CanvasRenderingContext2D} context
 * @param {'normal' | 'hover'} state
 * @param {Object.<string, string>} statusColors
 * @param {number} pointWidth
 */
function drawDataPoint(point, index, context, state, statusColors, pointWidth, clear = false) {
    const worst = point?.value?.worst || 'gray';
    const baseColor = statusColors[worst];
    const color = state === 'hover' ? lighten(baseColor, 0.2) : baseColor;
    const spacing = 2 - (pointWidth % 1);

    // Clearing the rect is necessary when hovering/unhovering to avoid antialiasing residue.
    if (clear) {
        context.clearRect(Math.floor(index * pointWidth), 0, Math.ceil(pointWidth - spacing), chartHeight);
    }

    context.fillStyle = color;
    context.fillRect(index * pointWidth, 0, pointWidth - spacing, chartHeight);
}

export function statusTrendColumnRenderer(rowData, column) {
    return <StatusTrend data={column.mapper(rowData, column)} width={column.width} />;
}
